import { useState } from 'react'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button } from '@fluentui/react-components'
import {
  ArrowRight20Regular,
  BrainCircuit20Regular,
  CheckmarkCircle16Filled,
  Database20Regular,
  Eye20Regular,
  Fingerprint20Regular,
  FolderOpen20Regular,
  Key20Regular,
  Laptop20Regular,
  LockClosed20Regular,
  NetworkCheck20Regular,
  ScanObject20Regular,
  Search20Regular,
  ShieldCheckmark20Regular,
  Tag20Regular,
} from '@fluentui/react-icons'
import { AppPreview } from '../components/AppPreview'
import { GovernanceFlow } from '../components/GovernanceFlow'
import { Marquee, SectionHead, SpotlightCard, Stat } from '../components/Primitives'
import { Reveal } from '../components/Reveal'
import { Topology, type DeploymentMode } from '../components/Topology'

const capabilities = [
  {
    icon: <Search20Regular />,
    title: 'One query, every source',
    text: 'Filenames, metadata, extracted text, OCR output and semantic passages — searched together across the workstation and policy-approved office storage.',
    points: ['SQLite FTS5 locally', 'PostgreSQL full text on the server', 'Sources report partial results instead of hiding them'],
  },
  {
    icon: <ScanObject20Regular />,
    title: 'Scanned paper becomes text',
    text: 'A bundled Tesseract runtime extracts English and Greek from scanned PDFs and images, keeping page-level status and retry evidence.',
    points: ['eng + ell traineddata bundled', 'Processing stays on the machine', 'Per-page success and failure records'],
  },
  {
    icon: <BrainCircuit20Regular />,
    title: 'Meaning, not just matching',
    text: 'Optional embeddings retrieve the right passage when the document uses different words than the person searching — including across languages.',
    points: ['pgvector with an HNSW index', 'recall@10 ≥ 0.95 against exact top-k', 'Similarity never grants access'],
  },
  {
    icon: <FolderOpen20Regular />,
    title: 'Files stay where they are',
    text: 'Libris indexes the folders an office already uses. Nobody uploads anything, nothing is copied into a second repository, and local search survives the network going down.',
    points: ['Incremental watching', 'Hash-based change detection', 'Full capability offline'],
  },
  {
    icon: <Eye20Regular />,
    title: 'Every result is explainable',
    text: 'File Details shows the matching passage and its page, plus metadata, tags, parsed content, provenance, processing errors and complete operation history.',
    points: ['Passage-level provenance', 'Indexing and OCR state', 'Activity and audit timeline'],
  },
  {
    icon: <ShieldCheckmark20Regular />,
    title: 'Permissions decided at answer time',
    text: 'Identity, endpoint policy and current Windows ACLs are evaluated before a protected result is displayed, and again before a file is opened or moved.',
    points: ['Keycloak roles', 'Enrolled-endpoint policy', 'Live NTFS/SMB ACL revalidation'],
  },
]

const pipeline = [
  ['Detect', 'A folder watcher or shared-root worker notices a new or changed file.'],
  ['Identify', 'Path, timestamps, size, type and a stable file identity are recorded.'],
  ['Extract', 'Supported formats are parsed; scanned pages are queued for OCR.'],
  ['Chunk', 'Text is divided into passages that keep page and chunk provenance.'],
  ['Index', 'Full-text terms and optional embeddings are written to the catalog.'],
  ['Serve', 'Each query is filtered by current identity and filesystem permissions.'],
]

const modes: Record<DeploymentMode, { label: string; title: string; description: string; facts: string[] }> = {
  local: {
    label: 'Standalone local',
    title: 'A complete workspace on one PC.',
    description:
      'Desktop and Agent maintain a local SQLite catalog with full-text search, OCR, tags, history and optional embeddings. There is no server to install, and nothing stops working when the network does.',
    facts: ['No server dependency', 'Catalog and OCR stay on the workstation', 'Installed by a single MSI'],
  },
  hybrid: {
    label: 'Managed hybrid',
    title: 'The same workspace, connected across the office.',
    description:
      'Every workstation keeps its full local capability and additionally enrolls with Libris Server. One search then covers local files, policy-approved endpoint catalogs and shared folders the server indexes directly.',
    facts: ['10–20 enrolled workstations', 'Merged local and server results', 'Central identity, policy and audit'],
  },
}

const securityLayers = [
  { icon: <Key20Regular />, title: 'User identity', text: 'Keycloak authenticates with OIDC and Authorization Code + PKCE. Libris validates issuer, audience, signature, expiry and role on every protected call.' },
  { icon: <Fingerprint20Regular />, title: 'Endpoint identity', text: 'A signed-in person does not make a workstation trusted. Enrolled agents present client certificates over mutual TLS and sign their request envelopes.' },
  { icon: <LockClosed20Regular />, title: 'File authorization', text: 'Catalog visibility and file access are separate decisions. Current Windows ACLs and source availability are revalidated before content is returned.' },
  { icon: <Database20Regular />, title: 'Data protection', text: 'Local secrets are DPAPI-protected, the central store is isolated on the server, and transport is encrypted in both directions.' },
]

const budgets: [string, string, string][] = [
  ['Local metadata / name first page', '1,000,000 files', 'p95 ≤ 75 ms'],
  ['Query to visible UI, including IPC and render', 'End to end', 'p95 ≤ 250 ms'],
  ['Server hybrid search', 'PostgreSQL + pgvector', 'p95 ≤ 250 ms'],
  ['Metadata discovery on NVMe', 'Indexing throughput', '≥ 50,000 files/min'],
  ['Idle agent working set after indexing', 'Background service', '≤ 150 MB'],
  ['Semantic recall@10 against exact top-k', 'EN/EL corpus', '≥ 0.95'],
]

const faqs = [
  {
    q: 'Do users upload documents into Libris?',
    a: 'No. Libris indexes files where they already live, on the workstation and on shared storage. Policy controls which metadata may synchronize to the office server; the files themselves are not copied into a second repository.',
  },
  {
    q: 'Does anything stop working when the server is unreachable?',
    a: 'No. A managed-hybrid workstation keeps the complete standalone-local experience — local search, OCR, tagging, file details and history. The server retains the last approved metadata for that endpoint but cannot open its files until it reconnects.',
  },
  {
    q: 'Can Libris reorganize files on its own?',
    a: 'No. Organization begins as an inspectable simulation. Execution requires a dry run, ACL revalidation, explicit approval, conflict handling, a durable journal, verification and a rollback path. AI-triggered file actions on endpoints remain disabled pending physical acceptance.',
  },
  {
    q: 'Is an OpenAI key required?',
    a: 'No. Embeddings are optional and off until an administrator or user supplies a credential. Everything else — indexing, full-text search, OCR, file details and governance analysis — runs without any external service.',
  },
]

export function Home() {
  const [mode, setMode] = useState<DeploymentMode>('hybrid')
  const selected = modes[mode]

  return (
    <main id="main">
      {/* --- hero ------------------------------------------------------------ */}
      <section className="hero grid-bg">
        <div className="shell-wide hero-layout">
          <div className="hero-copy">
            <Reveal as="p" variant="fade" className="kicker">
              Local-first · Windows 10 22H2 / 11 · EN + EL
            </Reveal>
            <h1 className="display-1">
              <Reveal as="span" variant="mask" index={1}>
                Search the office,
              </Reveal>
              <Reveal as="span" variant="mask" index={2}>
                not the folder.
              </Reveal>
            </h1>
            <Reveal as="p" variant="up" className="lead" index={3}>
              Libris indexes the documents an office already has — on every workstation and on shared storage — and
              returns the exact passage that answers the question, with permissions checked at the moment of the answer.
            </Reveal>
            <Reveal className="hero-actions" variant="up" index={4}>
              <Button
                className="cta-primary"
                appearance="primary"
                size="large"
                as="a"
                href="#capabilities"
                icon={<ArrowRight20Regular />}
                iconPosition="after"
              >
                See how it works
              </Button>
              <Button appearance="outline" size="large" as="a" href="/infrastructure">
                Read the architecture
              </Button>
            </Reveal>
            <Reveal as="ul" className="hero-facts mono" variant="fade" index={5}>
              <li>
                <CheckmarkCircle16Filled />
                Files never leave their folder
              </li>
              <li>
                <CheckmarkCircle16Filled />
                Full capability offline
              </li>
              <li>
                <CheckmarkCircle16Filled />
                Suggestion-first governance
              </li>
            </Reveal>
          </div>

          <Reveal variant="scale" index={2} className="hero-preview">
            <AppPreview />
          </Reveal>
        </div>
      </section>

      <Marquee
        items={[
          'Full-text search',
          'Greek + English OCR',
          'Semantic passages',
          'In-place indexing',
          'Windows ACL aware',
          'Duplicate detection',
          'Saved searches',
          'Audit journal',
          'Endpoint enrollment',
          'Rollback',
        ]}
      />

      {/* --- proof ----------------------------------------------------------- */}
      <section className="band-tight">
        <div className="shell">
          <Reveal className="stat-strip" variant="up">
            <Stat value={1000000} label="File benchmark corpus" note="synthetic · fixed seed" />
            <Stat value={75} prefix="≤ " suffix=" ms" label="p95 local first page" note="after warm-up" />
            <Stat value={2} label="Indexed and OCR'd languages" note="English · Ελληνικά" />
            <Stat value={0} label="Files moved without approval" note="governance is suggestion-first" />
          </Reveal>
        </div>
      </section>

      {/* --- capabilities ----------------------------------------------------- */}
      <section id="capabilities" className="band">
        <div className="shell">
          <SectionHead
            index="01"
            kicker="What Libris does"
            title={<>One workspace for the whole document lifecycle.</>}
            lead="Built around the way an office already works, not around another storage system the staff have to learn."
          />
          <div className="card-grid">
            {capabilities.map((capability, index) => (
              <SpotlightCard key={capability.title} index={index % 3}>
                <span className="card-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="card-icon">{capability.icon}</span>
                <h3>{capability.title}</h3>
                <p className="body">{capability.text}</p>
                <ul className="card-points">
                  {capability.points.map(point => (
                    <li key={point}>
                      <CheckmarkCircle16Filled />
                      {point}
                    </li>
                  ))}
                </ul>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </section>

      {/* --- pipeline --------------------------------------------------------- */}
      <section className="band pipeline-band">
        <div className="shell">
          <SectionHead
            index="02"
            kicker="Ingestion"
            title="What happens to a document."
            lead="Six stages between a file appearing on disk and a person getting an answer from it. The original never moves."
          />
          <ol className="pipeline">
            <Reveal as="i" variant="draw" className="pipeline-rail" aria-hidden={true} />
            {pipeline.map(([title, text], index) => (
              <Reveal as="li" key={title} variant="up" index={index}>
                <b className="mono">{String(index + 1).padStart(2, '0')}</b>
                <strong>{title}</strong>
                <small>{text}</small>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* --- governance -------------------------------------------------------- */}
      <section id="governance" className="band tinted">
        <div className="shell">
          <SectionHead
            index="03"
            kicker="Information governance"
            title="Organize with evidence, not guesswork."
            lead="Map the structure you already have, describe the structure you want, and review every proposed change before Libris is allowed to touch a single file."
          />
          <div className="gov-wrap">
            <GovernanceFlow />
          </div>
        </div>
      </section>

      {/* --- deployment --------------------------------------------------------- */}
      <section id="deployment" className="band">
        <div className="shell">
          <SectionHead
            index="04"
            kicker="Deployment"
            title="Start on one PC. Connect the office when it makes sense."
          />

          <div className="mode-switch" role="tablist" aria-label="Deployment mode">
            <span className="mode-thumb" data-mode={mode} aria-hidden="true" />
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'local'}
              className={mode === 'local' ? 'is-on' : undefined}
              onClick={() => setMode('local')}
            >
              <Laptop20Regular />
              Standalone local
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'hybrid'}
              className={mode === 'hybrid' ? 'is-on' : undefined}
              onClick={() => setMode('hybrid')}
            >
              <NetworkCheck20Regular />
              Managed hybrid
            </button>
          </div>

          <div className="deployment-panel">
            <div className="deployment-copy" key={mode}>
              <p className="kicker kicker-plain mono">{selected.label}</p>
              <h3 className="display-3">{selected.title}</h3>
              <p className="body">{selected.description}</p>
              <ul className="check-list">
                {selected.facts.map(fact => (
                  <li key={fact}>
                    <CheckmarkCircle16Filled />
                    {fact}
                  </li>
                ))}
              </ul>
              <Button appearance="outline" as="a" href="/infrastructure" icon={<ArrowRight20Regular />} iconPosition="after">
                Full infrastructure view
              </Button>
            </div>
            <Reveal variant="fade" className="deployment-diagram">
              <Topology mode={mode} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* --- security ----------------------------------------------------------- */}
      <section id="security" className="band inverted">
        <div className="shell">
          <div className="security-layout">
            <div>
              <Reveal as="p" variant="fade" className="kicker">
                Security
              </Reveal>
              <Reveal as="h2" variant="mask" className="display-2" index={1}>
                Access is verified at the moment it matters.
              </Reveal>
              <Reveal as="blockquote" variant="up" className="principle" index={2}>
                Search may rank a document. It never grants permission to see it.
              </Reveal>
              <Reveal variant="up" index={3}>
                <Button appearance="outline" as="a" href="/security" icon={<ArrowRight20Regular />} iconPosition="after">
                  Security architecture
                </Button>
              </Reveal>
            </div>
            <ul className="layer-list">
              {securityLayers.map((layer, index) => (
                <Reveal as="li" key={layer.title} variant="up" index={index}>
                  <span className="layer-icon">{layer.icon}</span>
                  <div>
                    <b>{layer.title}</b>
                    <p>{layer.text}</p>
                  </div>
                  <i className="mono">0{index + 1}</i>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* --- budgets ------------------------------------------------------------ */}
      <section id="budgets" className="band">
        <div className="shell">
          <SectionHead
            index="05"
            kicker="Engineering budgets"
            title="Numbers we hold ourselves to."
            lead="Release gates measured by a benchmark harness against a one-million-file synthetic corpus on a supported workstation. These are targets the build must meet — not marketing claims."
          />
          <Reveal className="budget-table" variant="up">
            <div className="budget-row is-head mono">
              <span>Workload</span>
              <span>Scope</span>
              <span>Budget</span>
            </div>
            {budgets.map(([workload, scope, budget], index) => (
              <div className="budget-row" key={workload} style={{ ['--i' as string]: index }}>
                <span>{workload}</span>
                <span className="mono">{scope}</span>
                <b className="mono">{budget}</b>
              </div>
            ))}
          </Reveal>
          <p className="table-note mono">
            Physical NTFS/SMB, two-machine LAN, backup/restore, installer and office-pilot gates must also pass before
            production activation.
          </p>
        </div>
      </section>

      {/* --- faq ----------------------------------------------------------------- */}
      <section className="band tinted">
        <div className="shell faq-layout">
          <SectionHead index="06" kicker="Common questions" title="Practical answers." />
          <Reveal variant="up" className="faq">
            <Accordion collapsible defaultOpenItems="0">
              {faqs.map((faq, index) => (
                <AccordionItem value={String(index)} key={faq.q}>
                  <AccordionHeader>{faq.q}</AccordionHeader>
                  <AccordionPanel>
                    <p>{faq.a}</p>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* --- contact -------------------------------------------------------------- */}
      <section id="contact" className="contact grid-bg">
        <div className="shell contact-inner">
          <Reveal variant="up">
            <p className="kicker">Plan your deployment</p>
            <h2 className="display-2">Make every document part of the answer.</h2>
            <p className="lead">
              Begin on a single workstation, or design a managed-hybrid office deployment with central identity, shared
              indexing and governance review.
            </p>
          </Reveal>
          <Reveal className="contact-actions" variant="up" index={1}>
            <a className="contact-card" href="/features">
              <span className="mono">Standalone local</span>
              <b>What it does on one PC</b>
              <ArrowRight20Regular />
            </a>
            <a className="contact-card" href="/infrastructure">
              <span className="mono">Managed hybrid</span>
              <b>How the office deployment fits together</b>
              <ArrowRight20Regular />
            </a>
            <a className="contact-card" href="/security">
              <span className="mono">Security review</span>
              <b>Identity, endpoints and authorization</b>
              <ArrowRight20Regular />
            </a>
          </Reveal>
          <Reveal as="p" className="contact-note mono" variant="fade" index={2}>
            <Tag20Regular />
            Windows x64 · signed MSI installs Desktop and Agent together · server installed separately
          </Reveal>
        </div>
      </section>
    </main>
  )
}
